import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle, RefreshCw, Loader2, ExternalLink, Check, X,
  Package, Save, Gift, Info, ChevronDown, ChevronUp, Activity, Search, Link2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

/* ══════════════════════════════════════════════════════════════════════════
   MM2 PENDING ITEMS — fiyatı belirlenemediği için KREDİLENMEMİŞ yatırmalar

   ⚠️ BU SAYFA MANUAL REVIEW DEĞİLDİR ve onun yerine geçmez.
      Manual Review  → /admin/mm2/trades/review   : takılmış ÇEKİMLER
      Bu sayfa       → /admin/mm2/pending-items   : kredilenmemiş YATIRMALAR
      İkisi ayrı kuyruk, ayrı uç, ayrı karar. Birleştirmeyin.

   Kuyruktaki her satır bir BORÇTUR: kullanıcı eşyayı fiziksel olarak bota
   verdi, karşılığını almadı. Kapatma sırası bilinçli olarak üç adım:

     1. Kataloğa fiyatıyla ekle   POST /admin/mm2/items/upsert      (ADMIN)
     2. Kullanıcıya ver           POST /admin/mm2/inventory/grant   (OWNER)
     3. Kaydı kapat               POST /admin/mm2/pending-items/resolve (OWNER)

   ⚠️ 3. adım EŞYA VERMEZ. 2'yi atlayıp 3'ü çağırmak, kullanıcının borcunu
      ödemeden silmek demektir.
   ⚠️ ARAYÜZ 3'Ü KİLİTLEMEZ — UYARIR. (Bu satır önceden "kilitler" diyordu;
      kod hiçbir zaman kilitlemedi. Yorumun kodu yanlış anlatması, bir sonraki
      kişinin var olmayan bir korumaya güvenmesi demektir.)
      Kilitlememek BİLİNÇLİ: eşya API'den ya da oyun içinden elle verilmiş
      olabilir; sert kilit o durumda kaydı kapatılamaz hâle getirirdi. Onay
      ekranı kaç eşyanın BU OTURUMDA verilmediğini açıkça yazar.

   ⚠️⚠️ ÇİFT ÖDEME RİSKİ — SUNUCU TARAFINDA İDEMPOTENS YOK.
      `/inventory/grant` her çağrıda eşyayı YENİDEN basar. "Verildi" bilgisi
      yalnızca BU OTURUMUN belleğinde (`granted` state); sayfa yenilenince
      sıfırlanır ve aynı kayıt yeniden "0/N verildi" görünür. Operatör
      farkında olmadan ikinci kez verirse kullanıcı eşyayı İKİ KEZ alır.
      Bu yüzden buton metni ve sayaç ısrarla "in this session" diyor —
      ekran bilmediği bir şeyi biliyormuş gibi göstermiyor.
      ⚠️ KALICI ÇÖZÜM sunucu tarafındadır: `grant` çağrısına bir idempotens
         anahtarı (ör. `pending_id + item_id`) eklenip tekrar eden çağrı
         yok sayılmalı. Panelde çözülemez — panelin hafızası yok.
   ══════════════════════════════════════════════════════════════════════════ */

/* ─── Eşleştirme yardımcıları — server.py ile BİREBİR aynı olmalı ─────────
   _mm2_norm_key      : re.sub(r"[^a-z0-9]+", "", s.lower())
   _mm2_collapse_repeats: ard arda tekrar eden kelimeyi teker
   _mm2p_slug_to_id   : ^[a-z0-9_]{2,48}$
   Buradaki bir sapma, ekranda "eşleşiyor" görünen bir adın sunucuda
   eşleşmemesi demek olur — yani yanlış teşhis. */
const normKey = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const collapseRepeats = (s) => {
  const out = [];
  for (const w of String(s ?? '').split(/\s+/).filter(Boolean)) {
    if (!out.length || out[out.length - 1].toLowerCase() !== w.toLowerCase()) out.push(w);
  }
  return out.join(' ');
};

const slugId = (s) => String(s ?? '').toLowerCase()
  .replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '').slice(0, 48);

const dashSlug = (s) => String(s ?? '').toLowerCase()
  .replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');

/* ⚠️⚠️ YAPIŞTIRILAN URL'DEN SON PARÇAYI AL — SUNUCUYLA AYNI DAVRANIŞ.
   🔴 Kapatılan hata (canlı testte ölçüldü): kutunun üstündeki yönerge
   “Open a source, find the item, and copy...” diyor, yani operatörün
   URL yapıştırması BEKLENEN davranış. Ama `dashSlug` doğrudan uygulanınca
      https://mm2values.app/values/starfish-gun
   → “https-mm2values-app-values-starfish-gun”
   oluyordu. Sonuçları:
     · sorgu hiçbir kaynağı bulamıyor (“neither source has a page”)
     · operatör yine de kaydederse BU ÇÖP SLUG kataloğa YAZILIYOR ve
       her senkron olmayan bir sayfayı arıyor → eşya kalıcı bayat
     · kaynak bağlantıları da .../values/https-mm2values-app-... oluyor
   Sunucudaki `_mm2_parse_price_slug` tam URL'i ZATEN destekliyor; panel
   onu sunucuya ulaşmadan bozuyordu.
   ⚠️ Alan adı DOĞRULAMASI burada YAPILMAZ — o sunucunun işi (yabancı alan
      adını reddediyor). Burada yalnızca son yol parçası alınır; yanlış
      siteden kopyalanmışsa sunucu söyler. */
/* ⚠️ KAYNAK LİSTESİ — `slugInput` ondan önce geldiği için BURADA tanımlı.
   Aşağı taşımak, alan adı listesini elle ikinci kez yazmayı gerektirirdi.
   ⚠️ Kaynaklar fiyat motorunun kullandıklarıyla AYNI olmalı. Başka bir
      yerden fiyat girmek, bir sonraki senkron turunda büyük sapma olarak
      görünür ve ya teyide takılır ya needs_review'a düşer. */
const SOURCES = [
  { label: 'mm2values.app', url: (n) => `https://mm2values.app/values/${dashSlug(n)}` },
  { label: 'mm2.rocks',     url: (n) => `https://mm2.rocks/items/${dashSlug(n)}` },
];

/* ⚠️⚠️ ALAN ADLARI `SOURCES`TAN TÜRETİLİR — ELLE YAZILMAZ.
   🔴 Kapatılan tutarsızlık: bu liste elle yazılmıştı ve yorumu “SOURCES
   ile aynı listeden türer” diyordu. Türemiyordu. Bir kaynak eklenince
   ya da alan adı değişince `SOURCES` güncellenir, bu liste ESKİ kalırdı:
   yeni kaynağın URL'i “yabancı” sayılıp ham gönderilir, sunucu
   reddeder ve operatör sebebini anlamazdı.
   Aynı hata sınıfı bu dosyada üç kez yakalandı (yorumun kodu yanlış
   anlatması); burada kaynağı TEK yapıyoruz. */
const SOURCE_HOSTS = SOURCES.map(s => {
  const m = String(s.url('x')).match(/^https:\/\/([^/]+)/i);
  return m ? m[1].toLowerCase().replace(/^www\./, '') : '';
}).filter(Boolean);

/* ⚠️⚠️ BU FONKSİYON YALNIZCA OPERATÖRÜN YAZDIĞI/YAPIŞTIRDIĞI GİRDİ İÇİN.
   🔴 Kapatılan sapma: eşya ADINDAN varsayılan slug türetirken de
   kullanılıyordu. Ad eğik çizgi içeriyorsa (“Bat / Batwing”,
   “Candy/Cane 2019”) URL sanılıp SON PARÇAYA iniyordu:
        “Bat / Batwing” → “batwing”   (doğrusu “bat-batwing”)
        “A/B/C”         → “c”
   Operatör yanlış tahmini görüp doğru sanarak sorgular, kaynak bulunmaz,
   ya da daha kötüsü BAŞKA bir eşyanın sayfasını bulur.
   ⚠️ Ad → slug türetmesi `dashSlug` ile yapılır (eğik çizgi tire olur).
      URL ayrıştırması YALNIZCA operatör girdisine uygulanır. */
const slugInput = (s) => {
  let t = String(s ?? '').trim();
  if (!t) return '';
  /* ⚠️⚠️ YABANCI ALAN ADINDAN SON PARÇA ALINMAZ.
        Sunucudaki `_mm2_parse_price_slug` bunu AÇIKÇA reddediyor ve
        gerekçesini yazıyor: yanlış siteden kopyalanan bir slug, ASIL
        kaynakta BAŞKA bir eşya olabilir → sessizce yanlış fiyat.
        Panelin körlemesine son parçayı alması, o korumayı ETKİSİZ kılardı:
        sunucuya düz bir slug gider, sunucu alan adını hiç göremez.
        Bu yüzden:
          · TANINAN kaynak (SOURCE_HOSTS) → son parça alınır
          · TANINMAYAN alan adı           → OLDUĞU GİBİ bırakılır, sunucu
            reddeder ve operatöre “yabancı alan adı” der
          · alan adı YOK (çıplak yol)     → son parça alınır (sunucu da öyle)
        ⚠️ Alan adı listesi burada YALNIZCA “son parçayı al” kararı için;
           asıl doğrulama sunucudadır. İkisi ayrışırsa sunucu kazanır. */
  const q = t.split(/[?#]/)[0];
  const hostM = q.match(/^(?:[a-z][a-z0-9+.-]*:)?\/\/([^/]+)/i)
             || q.match(/^([^\s/]+\.[^\s/]+)\//);
  if (hostM) {
    const host = hostM[1].toLowerCase().replace(/^www\./, '');
    if (!SOURCE_HOSTS.includes(host)) return t;   // yabancı → sunucu reddetsin
  }
  if (q.includes('/')) {
    const parts = q.split('/').filter(Boolean);
    t = parts.length ? parts[parts.length - 1] : '';
  } else {
    t = q;
  }
  return dashSlug(t);
};

/* ⚠️⚠️ TİP GÜVENLİ OKUYUCULAR — KATALOG BELGESİ HER TÜRLÜ OLABİLİR.
   `existing` doğrudan /admin/mm2/items/all'dan, yani veritabanındaki HAM
   belgeden geliyor (projeksiyon yalnızca `_id`yi atıyor). Devir notu §5.7:
   bozuk veri VARSAYIMSAL DEĞİL — sözlük `icon_ids` üretimde görüldü.
   🔴 Ölçülen üç çökme (8000 turluk fuzz):
      · (existing.name || raw).trim()      → name sayı/sözlükse `.trim` yok
      · [...(existing.icon_ids || [])]     → sözlük yayılamaz (not iterable)
      · (existing.aliases || []).map(...)  → metin/sözlükte `.map` yok
   React'te atılan istisna AĞACI SÖKER: kuyruk ekranı BEYAZ kalır. Burada
   duran şey kullanıcı BORCU — ekranın ayakta kalması zorunlu. */
const asArray = (v) => (Array.isArray(v) ? v : []);
const asText  = (v) => (typeof v === 'string' ? v : '');
/* Katalogdaki `icon_ids` sayı da olabilir; sunucu metin bekliyor. */
const asIdList = (v) => asArray(v)
  .filter(x => typeof x === 'string' || typeof x === 'number')
  .map(x => String(x))
  .filter(x => /^[0-9]{1,20}$/.test(x));

const relTime = (iso) => {
  if (!iso) return '—';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (Number.isNaN(s)) return '—';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const num = (n) => Number(n || 0).toLocaleString();

/* ⚠️ Adminpanel.js'teki `getUserRank` ile BİREBİR AYNI olmak zorunda.
      Sapması, ekranda "owner" rozeti taşıyan birinin bu sayfada owner
      sayılmaması demek olurdu. */
const rankOf = (u) => {
  if (!u) return 0;
  if (u.is_owner) return 4;
  if (u.is_admin) return 3;
  if (u.is_moderator) return 2;
  return 1;
};

/* ⚠️ PROP GELMEZSE KENDİ HESAPLAR.
      `viewerRank` verilmediğinde değeri `undefined` olur ve `undefined >= 3`
      FALSE döner — yani owner olan biri bile bütün butonları kapalı görür,
      hiçbir hata mesajı olmadan. Tek satırlık bir JSX unutkanlığının sayfayı
      sessizce öldürmesi kabul edilebilir değil; oturumdaki kullanıcıdan
      türetip devam ediyoruz. */
const resolveRank = (prop, user) =>
  (typeof prop === 'number' && Number.isFinite(prop)) ? prop : rankOf(user);

/* Sunucu 400'de düz metin, 422'de Pydantic dizisi döner. İkisini de oku:
   ham nesneyi ekrana basmak operatöre "[object Object]" göstermekti. */
const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(' · ');
  return err?.message || fallback;
};


/* ════════════════════════════════════════════════════════════
   KATALOG DİZİNİ
   ════════════════════════════════════════════════════════════ */
const buildIndex = (items) => {
  const map = new Map();
  /* ⚠️⚠️ HAM KATALOG BELGESİ — HER TÜRLÜ OLABİLİR.
     `items` doğrudan /admin/mm2/items/all'dan geliyor; projeksiyon yalnızca
     `_id`yi atıyor, yani veritabanında ne varsa o.
     🔴 Ölçülen üç çökme (8000 turluk fuzz):
        · `for (const it of items || [])` → `items` liste değilse iterable
          değil (sözlük/sayı) → TypeError
        · dizide `null` eleman → `it.id` okunamaz → TypeError
        · `(it.aliases || []).map()`     → `aliases` metin/sözlükse `.map` yok
     Üçüncüsü, `aliasBuild` içinde kapatılan hatanın BİREBİR İKİZİ — biri
     düzeltilip diğeri atlanmıştı. Bu fonksiyon TÜM ekranın dizinini kuruyor:
     burada atılan istisna React ağacını söker ve kuyruk BEYAZ kalır. */
  /* ⚠️⚠️ ASSETID DİZİNİ DE KURULUR — ad dizini TEK BAŞINA YETMİYOR.
     🔴 Kapatılan uyumsuzluk: bu ekran eşyayı YALNIZCA ada göre arıyordu,
     oysa kimlik ekranı `asset in by_icon` ölçütünü kullanıyor (server.py).
     Sonuç: operatör eşyayı kimlik ekranından kataloğa ekliyor, buraya
     geliyor ve satır hâlâ “Not in catalog” diyordu — çünkü BOTUN
     BİLDİRDİĞİ AD katalogdaki addan farklıydı (“Clownfish” ↔
     “Clownfish Gun”). Operatör yeni eşya yaratmaya çalışıyor, sunucu da
     haklı olarak “Name/alias collides with 'clownfish_gun'” deyip
     reddediyordu: iki eşya aynı anahtarı sahiplenemez.
     assetId ad belirsizliğini çözen TEK güvenilir ayırt edici; deposit yolu
     da (server.py `_mm2_catalog.by_icon`) önce ona bakıyor. Panel de öyle
     yapmalı ki iki ekran AYNI eşyayı aynı şekilde görsün. */
  const byIcon = new Map();
  for (const it of asArray(items)) {
    if (!it || typeof it !== 'object') continue;
    const keys = new Set([normKey(it.id), normKey(it.name),
                          ...asArray(it.aliases)
                            .filter(a => typeof a === 'string' || typeof a === 'number')
                            .map(normKey)]);
    keys.delete('');
    for (const a of asArray(it.icon_ids)) {
      const s = String(a ?? '');
      if (!/^[0-9]{1,20}$/.test(s)) continue;
      /* ⚠️ Aynı assetId iki eşyada duruyorsa (yarış artığı) HİÇBİRİNİ
            seçmeyiz: sunucu da o anahtarı çakışma sayıp düşürüyor.
            Rastgele birini seçmek yanlış eşyayı grant ettirebilirdi. */
      if (byIcon.has(s) && byIcon.get(s)?.id !== it.id) byIcon.set(s, null);
      else if (!byIcon.has(s)) byIcon.set(s, it);
    }
    if (!keys.size) continue;
    for (const k of keys) {
      if (!map.has(k)) map.set(k, []);
      const arr = map.get(k);
      if (!arr.some(x => x.id === it.id)) arr.push(it);
    }
  }
  map.__byIcon = byIcon;
  return map;
};

/* Botun bildirdiği ad → katalogdaki durumu.
   ⚠️ Doğrudan anahtar tutmazsa tekrar-teklenmiş hâli de denenir; sunucudaki
      deposit yolu da tam olarak bu iki denemeyi yapıyor. */
const lookupName = (index, rawName, asset) => {
  /* ⚠️⚠️ ÖNCE assetId. Bot ne ad bildirirse bildirsin, assetId katalogda
        duruyorsa eşya BULUNMUŞTUR. Sıra ada göre olsaydı, adı tutmayan ama
        assetId'si bağlı eşya “yeni” görünür ve operatör kopya yaratmaya
        çalışırdı (sunucu reddeder, iş tıkanır). */
  const _byIcon = index && index.__byIcon;
  if (asset && _byIcon && _byIcon.has(String(asset))) {
    const it = _byIcon.get(String(asset));
    if (it) {
      const usable = it.active !== false && Number(it.value || 0) > 0;
      return { status: usable ? 'ready' : 'disabled', item: it,
               key: normKey(rawName), viaAsset: true };
    }
    /* null = aynı assetId birden fazla eşyada; ada düş ve operatöre söyle. */
  }
  const k1 = normKey(rawName);
  const k2 = normKey(collapseRepeats(rawName));
  const hits = [];
  for (const k of new Set([k1, k2].filter(Boolean))) {
    for (const it of (index.get(k) || [])) {
      if (!hits.some(x => x.id === it.id)) hits.push(it);
    }
  }
  if (!k1) return { status: 'unmatchable', key: k1 };
  if (hits.length > 1) return { status: 'collision', matches: hits, key: k1 };
  if (hits.length === 1) {
    const it = hits[0];
    const usable = it.active !== false && Number(it.value || 0) > 0;
    return { status: usable ? 'ready' : 'disabled', item: it, key: k1 };
  }
  return { status: 'new', key: k1 };
};

const STATUS_CFG = {
  new:         { label: 'Not in catalog', cls: 'text-amber-300 bg-amber-500/10 border-amber-500/30' },
  disabled:    { label: 'In catalog but unusable', cls: 'text-orange-300 bg-orange-500/10 border-orange-500/30' },
  collision:   { label: 'Name collision', cls: 'text-red-300 bg-red-500/15 border-red-500/30' },
  ready:       { label: 'Priced — ready to grant', cls: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30' },
  unmatchable: { label: 'Name has no letters or digits', cls: 'text-red-300 bg-red-500/15 border-red-500/30' },
};

/* ════════════════════════════════════════════════════════════
   FİYAT SENKRONU ŞERİDİ
   Kuyruğun asıl sebebi çoğu zaman burada görünür: senkron ölmüşse
   fiyat dizini boştur ve HER yeni eşya bu kuyruğa düşer.
   ════════════════════════════════════════════════════════════ */
const SyncStrip = ({ token }) => {
  const [st, setSt] = useState(null);
  /* ⚠️⚠️ KAYNAK ADLARI METNE İNDİRGENİR.
     🔴 Kapatılan çökme (gerçek render fuzz'ı): `primary_source` /
     `secondary_source` doğrudan React'e çocuk olarak veriliyordu. Sözlük ya
     da liste gelirse React “Objects are not valid as a React child” atar.
     Bu şerit ekranın EN ÜSTÜNDE: burada atılan istisna ağacı söker ve
     KUYRUĞUN TAMAMI beyaz kalır — orada duran şey kullanıcı borcudur.
     ⚠️ Değerler sunucu yapılandırmasından geliyor; bugün metin olmaları
        muhtemel, ama bu şeridin ayakta kalması buna bağlı olmamalı. */
  const src1 = asText(st?.primary_source);
  const src2 = asText(st?.secondary_source);

  useEffect(() => {
    axios.get(`${API}/admin/mm2/price-sync/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSt(r.data)).catch(() => setSt({ error: true }));
  }, [token]);

  if (!st) return null;
  if (st.error) return null;

  const bad = !st.enabled || st.last_ok === false || !st.last_run;

  return (
    <div className={`flex items-start gap-2.5 px-4 py-3 rounded-xl border ${
      bad ? 'border-red-500/30 bg-red-500/10' : 'border-[#2A2D31] bg-[#121417]'}`}>
      <Activity className={`w-4 h-4 shrink-0 mt-0.5 ${bad ? 'text-red-400' : 'text-zinc-500'}`}/>
      <div className="min-w-0 text-xs leading-relaxed">
        {!st.enabled ? (
          <p className="text-red-300">
            <span className="font-bold">Price sync is off.</span>{' '}
            Nothing refreshes the price index, so every new item lands in this queue.
            Set <code className="text-[11px]">MM2_PRICE_SYNC_ENABLED=true</code> and restart.
          </p>
        ) : st.last_ok === false ? (
          <p className="text-red-300">
            <span className="font-bold">Last sync run failed</span> ({relTime(st.last_run)}).
            {st.last_error ? <span className="text-zinc-400"> {String(st.last_error).slice(0, 180)}</span> : null}
            {' '}Until it succeeds, unknown items keep queuing here.
          </p>
        ) : (
          /* ⚠️ EŞİKLERİ KİPTEN BAĞIMSIZ YAZMAK YANLIŞ BİLGİDİR.
                `trust_primary` açıkken çapraz tolerans REDDETMİYOR, sadece
                gerekçeye yazılıyor; auto-add tavanı da yalnızca birincil
                kaynağın listelemediği eşyalara uygulanıyor. İkisini koşulsuz
                göstermek operatöre "iki kaynak doğrulanıyor" dedirtirdi —
                var sanılan bir koruma, olmayan korumadan tehlikelidir. */
          <p className="text-zinc-500">
            Price sync ok · last run {relTime(st.last_run)} ·{' '}
            {st.trust_primary ? (
              <>
                <span className="text-zinc-300 font-bold">{src1 || "the primary source"}</span> decides prices.
                {src2
                  ? <> <span className="text-zinc-400">{src2}</span> is only used for items
                      it does not list, capped at {num(st.thresholds?.new_item_auto_add_max_value)}.</>
                  : ' No second source configured.'}
                {' '}Disagreement is recorded, not blocked.
              </>
            ) : (
              <>
                <span className="text-zinc-400">{src1 || "the primary source"}</span>
                {src2
                  ? <> + <span className="text-zinc-400">{src2}</span> must agree within{' '}
                      {st.thresholds?.new_item_cross_tolerance_pct}%</>
                  : ' (no cross-check)'}
                {' · '}auto-add cap {num(st.thresholds?.new_item_auto_add_max_value)}
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   TEK EŞYA SATIRI
   ════════════════════════════════════════════════════════════ */
/* ════════════════════════════════════════════════════════════
   TEK EŞYA SATIRI

   Akış bilinçli olarak bu sırada — operatör FİYAT YAZMAZ:
     1. doğru arama adını (slug) gir  → "Check sources"
     2. iki kaynağın dediğini gör     → "Save to catalog"
     3. eşyayı kullanıcıya ver        → "Grant"

   Slug, kaydedilen eşyanın `aliases` alanına yazılıyor. KALICI EŞLEME
   BUDUR: `_mm2p_plan_updates` eşleştirmeyi {id, name} ∪ aliases üzerinden
   yapıyor, yani bu eşya bundan sonra her turda o adla aranır.
   ════════════════════════════════════════════════════════════ */
const ItemRow = ({ rawName, rowKey, record, index, multiplier, viewerRank,
                   granted, onGranted, onCatalogChanged, thumbs }) => {
  const { token, user } = useAuth();

  /* ⚠️⚠️ BU SATIRIN GERÇEK assetId'Sİ.
        `record.detail` sunucuda `names` ile İNDEKS HİZALI yazılır ve hizalama
        bozuksa BOŞ gönderilir (server.py: `_detail` kapısı) — yani burada
        `detail[rowKey]` ya doğru eşyaya aittir ya da hiç yoktur. Ada göre
        aramak YANLIŞ olurdu: bu kuyruğun var olma sebebi zaten AYNI ADI
        TAŞIYAN BİRDEN FAZLA EŞYA olması.
        ⚠️ assetId, ad belirsizliğini çözen TEK güvenilir ayırt edicidir:
           kataloğa `icon_ids` olarak bağlanırsa bu eşyanın sonraki
           deposit'leri ADA hiç bakılmadan doğrudan eşleşir ve bir daha bu
           kuyruğa DÜŞMEZ. */
  const rowDetail = useMemo(() => {
    const d = Array.isArray(record?.detail) ? record.detail[rowKey] : null;
    return d && typeof d === 'object' ? d : null;
  }, [record, rowKey]);

  const rowAsset = useMemo(() => {
    const a = rowDetail ? rowDetail.asset : null;
    return /^[0-9]{1,20}$/.test(String(a ?? '')) ? String(a) : '';
  }, [rowDetail]);

  /* ⚠️ `found` rowAsset'e BAĞIMLI olduğu için ondan SONRA tanımlanır.
        `const` hoisting yapmaz: önce yazılsaydı ReferenceError verirdi. */
  const found = useMemo(() => lookupName(index, rawName, rowAsset),
                        [index, rawName, rowAsset]);

  /* ⚠️ Görsel adresi ARTIK KAYIT SEVİYESİNDE, tek istekte alınıyor
        (bkz. PendingRecord). Satır başına istek atmak, 30 eşyalık bir
        yatırmada 30 ayrı çağrı demekti; sunucu modeli zaten 100'lük toplu
        istek için yazılmış. */
  const assetThumb = (rowAsset && thumbs && thumbs[rowAsset]) || '';

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  /* ⚠️⚠️ ALAN KATALOGDAKİ MEVCUT SLUG'DAN BAŞLAR — ham addan DEĞİL.
        🔴 Kapatılan veri kaybı: alan `dashSlug(rawName)` ile başlıyordu.
        Eşyanın ÇALIŞAN bir `price_slug`'ı varsa kutu ondan FARKLI bir değer
        gösteriyordu; operatör gördüğünü doğru sanıp kaydedince çalışan slug
        yanlışıyla EZİLİYORDU. Ekranın gösterdiği, kaydedilmiş olanla aynı
        olmalı — aksi hâlde ekran operatöre yanlış bir dünya gösterir. */
  const [slug, setSlug] = useState(
    () => (found.item?.price_slug
      ? slugInput(found.item.price_slug)   // kayıtlı slug: zaten temiz
      : dashSlug(rawName)));               // AD: eğik çizgi KORUNUR

  /* ⚠️⚠️ `useState` BAŞLATICISI YALNIZCA MOUNT'TA ÇALIŞIR.
        🔴 Kapatılan eksik düzeltme: alanı `found.item?.price_slug` ile
        başlatmak, KATALOG O AN YÜKLENMİŞSE işe yarıyordu. Kuyruk ile katalog
        AYRI isteklerle geliyor; katalog sonra gelirse mount anında
        `found.item` YOK ve kutu ham addan başlıyor — yani düzeltme o
        sıralamada hiç uygulanmıyordu ve ekran yine çalışan slug'dan farklı
        bir değer gösteriyordu.
        ⚠️ Senkron YALNIZCA operatör alana DOKUNMADIYSA yapılır; yazdığı
           şeyi katalog yüklendi diye ezmek daha kötü olurdu. */
  const [slugTouched, setSlugTouched] = useState(false);
  useEffect(() => {
    if (slugTouched) return;
    const s = slugInput(found.item?.price_slug || '');   // yalnızca kayıtlı slug
    if (s) setSlug(s);
  }, [found.item, slugTouched]);
  const [slug2, setSlug2] = useState('');
  const [imgSlug, setImgSlug] = useState('');
  const [probe, setProbe] = useState(null);   // /price-lookup sonucu
  const [manual, setManual] = useState('');   // kaynak listelemiyorsa son çare
  const [confirmHigh, setConfirmHigh] = useState(false);

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  /* ⚠️ `cfg` undefined kalırsa aşağıdaki `cfg.cls` çöker. Bugün lookupName
        yalnızca STATUS_CFG'de olan beş durumu üretiyor (fuzz'da doğrulandı),
        ama yeni bir durum eklenip buraya yazılmazsa ekran tümüyle düşerdi —
        yedek, o hatayı sessiz bir rozete indirger. */
  const cfg = STATUS_CFG[found.status]
    || { cls: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/30', label: '?' };
  /* ⚠️ `target` hem EKRANA çiziliyor hem de /inventory/grant gövdesinde
        SUNUCUYA gidiyor. Nesne olursa React çöker, sunucu da 422 verir. */
  const target = asText(record.grant_to) || asText(record.user_id);
  const toHouse = target === '__mm2_house__';
  /* ⚠️ İKİ AYRI YETKİ, BİLEREK AYRI:
        · canLookup (ADMIN) — kaynağa bakıp doğru adı bulmak. Yazma yok.
        · canWrite  (OWNER) — kataloğa değer yazmak ve eşya vermek.
        Sunucu /items/upsert'ü OWNER'a çekti çünkü `value` doğrudan paraya
        çevriliyor. Burada da aynı sınırı çiziyoruz ki admin butona basıp
        403 yemesin — reddedilen bir buton, kapalı bir butondan daha kötüdür:
        işin yapıldığını sanıp sayfayı kapatabilir. */
  const rank = resolveRank(viewerRank, user);
  /*  ⚠️⚠️ RÜTBE ÖLÇEĞİ: OWNER = 4, ADMIN = 3, MOD = 2, USER = 1.
        (server.py: RANK_OWNER = 4 · RANK_ADMIN = 3 · rankOf() ile aynı.)
        🔴 Kapatılan REGRESYON: bu eşikler bir ara `>= 5` yapılmıştı, çünkü
        OWNER'ın 5 olduğu VARSAYILMIŞTI. Owner rütbesi 4 olduğu için CANLIDA
        owner bile HİÇBİR düğmeye basamadı — ekran tamamen kilitlendi.
        Testler bunu yakalayamadı: koşum sabiti de yanlış (`RANK_OWNER: 5`)
        alınmıştı, yani test kendi hatasını doğruluyordu.
        ⚠️ Bir eşik değiştirilecekse ölçek server.py'den OKUNARAK doğrulanır;
           varsayılmaz.
        Uçların gerçek rütbeleri:
           /admin/mm2/price-lookup          → RANK_ADMIN (3)
           /admin/mm2/items/upsert          → RANK_OWNER (4)
           /admin/mm2/inventory/grant       → RANK_OWNER (4)
           /admin/mm2/pending-items/resolve → RANK_OWNER (4) */
  const canLookup = rank >= 3;   // kaynağa bak   (price-lookup · ADMIN)
  const canWrite  = rank >= 4;   // fiyat yaz     (items/upsert · OWNER)
  const canGrant  = rank >= 4;   // eşya ver      (inventory/grant · OWNER)

  /* Katalogdaki eşyanın alanları — upsert gövdeyi `$set: {**model_dump()}`
     ile yazdığı için EKSİK GÖNDERİLEN ALAN ESKİ DEĞERİNİ KORUMAZ, modelin
     varsayılanıyla EZİLİR. Boş image_url görseli siler, boş aliases ise
     eşleştirme takma adlarını siler — yani tam da kurmaya çalıştığımız
     kalıcı eşlemeyi. Mevcut eşyada her alan buradan taşınıyor. */
  const existing = found.item || null;

  const effValue = probe?.value ?? (manual ? Number(manual) : null);
  const mult = multiplier ?? probe?.value_multiplier ?? null;
  const scaled = (mult && effValue > 0) ? effValue * mult : null;
  /* Parmak kayması koruması — GÜVENLİK KONTROLÜ DEĞİL, sunucu tarafında
     karşılığı yok. Asıl sınır rütbe: /items/upsert artık OWNER istiyor. */
  const highValue = effValue !== null && effValue >= 100000;

  /* ⚠️ MEVCUT GÖRSELİ EZME.
        `image_candidates` doğrulanmış değil, ADDAN ÜRETİLMİŞ tahmindir ve
        geçerli her slug için DOLU döner. Koşulsuz yazsaydık,
        mm2_download_images.py'nin kurduğu yerel kopyayı (/items/x.png) her
        kayıtta uzak bir tahminle değiştirirdik: yavaşlar, üçüncü tarafın
        hotlink korumasına bağlanır ve senkron kodunun ısrarla uyardığı geri
        adımın ta kendisi olur. Sıra: operatörün açıkça yazdığı ad → mevcut
        görsel → kaynak sayfasının verdiği gerçek adres → tahmin. */
  const imageUrl = useMemo(() => {
    const typed = slugInput(imgSlug);
    if (typed.length >= 2 && probe?.image_candidates?.length) return probe.image_candidates[0];
    if (asText(existing?.image_url)) return existing.image_url;
    return probe?.image_from_source || probe?.image_candidates?.[0] || '';
  }, [imgSlug, probe, existing]);

  /* ⚠️ EKRANDA GÖSTERİLEN görsel `imageUrl`DEN AYRI TUTULUR.
        `imageUrl` kataloğa YAZILACAK adrestir ve yukarıdaki sıra bilinçlidir
        (yerel kopyayı ezmemek için). Ama operatörün EKRANDA görmesi gereken
        şey, botun getirdiği GERÇEK eşyanın resmidir — addan üretilmiş bir
        tahmin değil. İkisini aynı değişkende birleştirmek, ya yanlış resmi
        gösterir ya da yerel kopyayı ezerdi. */
  /* ⚠️⚠️ `src`'YE GİDEN ADRES SÜZÜLÜR — SUNUCUDAKİ KURALIN AYNISI.
        `imageUrl` ham katalog değerinden gelebiliyor (`existing.image_url`)
        ve o belge, alan doğrulayıcısı EKLENMEDEN ÖNCE yazılmış olabilir.
        `javascript:` / `data:` gibi bir değer tarayıcıda `img src` üzerinden
        çalışmasa da, sunucunun kabul ettiği kümeyi panelin de uygulaması
        gerekir: iki taraf ayrışırsa hangisinin doğru olduğu belirsizleşir.
        İzin verilen: `https://…` ya da tek `/` ile başlayan aynı-köken yol.
        Reddedilen: `//host` (protokol-göreli), `http://`, `javascript:`,
        `data:` ve şeması olmayan her şey. */
  const safeSrc = (u) => {
    const v = asText(u);
    if (!v) return '';
    if (/^https:\/\/[^\s"'<>]+$/.test(v)) return v;
    if (!v.includes('//') && /^\/[A-Za-z0-9_\-][A-Za-z0-9_\-./]*$/.test(v)) return v;
    return '';
  };
  const previewUrl = safeSrc(assetThumb) || safeSrc(imageUrl);

  /* ⚠️ NADİRLİĞİ DÜŞÜRME. `_mm2p_norm_rarity` tanımadığı her şeyi "common"a
        indiriyor, yani kaynak nadirliği bildirmediğinde de "common" döner.
        Koşulsuz yazsaydık, katalogdaki doğru "godly" her fiyat güncellemesinde
        "common"a düşerdi. Nadirlik yalnızca RENK belirliyor, değeri değil —
        ama sessizce yanlışa dönmesi yine de hata. */
  const rarity = (probe?.rarity && probe.rarity !== 'common')
    ? probe.rarity
    : (existing?.rarity || probe?.rarity || 'common');

  const aliasBuild = useMemo(() => {
    /* ⚠️ `asArray`: bozuk `aliases` (metin/sözlük) `.map`te çökertiyordu.
          Metin olsaydı `for...of` onu HARFLERE bölüp her harfi takma ad
          yapardı — çökmese bile katalog anahtarlarını kirletirdi. */
    const list = new Set(asArray(existing?.aliases)
      .filter(x => typeof x === 'string' && x.trim()).map(String));
    for (const a of asArray(probe?.suggested_aliases)) {
      if (typeof a === 'string' && a.trim()) list.add(a);
    }
    /* Botun bildirdiği ad, kaydedilecek addan farklıysa o da takma ad olur —
       yoksa aynı eşya yarın yine kuyruğa düşer. */
    const nm = asText(existing?.name) || rawName;
    if (normKey(rawName) && normKey(nm) !== normKey(rawName)) list.add(String(rawName).slice(0, 64));
    const all = [...list].filter(Boolean);
    /* ⚠️ SUNUCU 20'DEN FAZLASINI REDDEDER (MM2AdminItem.aliases max_length=20,
          ve bu sınır field_validator'dan ÖNCE çalışıyor — kırpma değil 422).
          Kesme sırası önemli: mevcut takma adlar BAŞTA olduğu için hayatta
          kalır, taşan YENİ ad düşer. Ters sırada, çalışan bir eşlemeyi
          sessizce silmiş olurduk. */
    return { list: all.slice(0, 20), dropped: all.length - Math.min(all.length, 20) };
  }, [existing, probe, rawName]);
  const aliasList = aliasBuild.list;

  const probeSources = async () => {
    const s = slugInput(slug);
    if (s.length < 2) return toast.error('Enter the item name as it appears in the source URL.');
    setBusy('probe');
    try {
      const r = await axios.post(`${API}/admin/mm2/price-lookup`, {
        slug: s,
        slug_secondary: slugInput(slug2),
        image_slug: slugInput(imgSlug),
      }, auth);
      setProbe(r.data);
      setConfirmHigh(false);
      if (!r.data.primary && !r.data.secondary) {
        toast.error('Neither source has a page under that name. Open the links and copy the exact name from the URL.', { duration: 9000 });
      } else if (!r.data.auto_ok) {
        toast(`Found, but the engine would not auto-price it: ${r.data.reason}`, { duration: 9000 });
      } else {
        toast.success(`${num(r.data.value)} — ${r.data.reason}`);
      }
    } catch (err) {
      toast.error(errText(err, 'Lookup failed.'), { duration: 9000 });
    } finally { setBusy(''); }
  };

  /* ⚠️⚠️ SLUG YALNIZCA DOĞRULANMIŞSA GÖNDERİLİR.
        🔴 Kapatılan regresyon (ilk yazımda vardı): `slug` alanının
        VARSAYILANI `dashSlug(rawName)`. Koşulsuz gönderince, operatör slug
        kutusuna HİÇ DOKUNMASA bile uydurulmuş bir slug yazılıyordu. İki zarar:
          · UYDURMA VERİ — kaynakta o sayfa yoksa senkron her turda boşa arar
            ve eşya `price_sync_missing` olur; slug hiç yazılmasa ADA göre
            eşleşme şansı vardı. (Aynı hata sınıfı: `value_seen: d.value || 1`)
          · YENİ RET — uydurulan slug başka bir eşyanın anahtarıyla çakışırsa
            sunucu §5.4 zırhıyla 400 döner. ÖLÇÜLDÜ: eski kod slug
            göndermediği için sorunsuz kaydedilen bir kayıt, operatör slug'a
            dokunmadığı hâlde reddediliyordu.
        `probe` yalnızca kaynak sorgusu BAŞARILI olunca dolar ve slug her
        değiştiğinde `setProbe(null)` ile sıfırlanır — yani doluysa GEÇERLİ
        slug'a aittir. Doğrulanmamışsa alan hiç gönderilmez ve sunucu mevcut
        slug'ı KORUR (bkz. MM2AdminItem.price_slug: None = dokunma). */
  const slugVerified = !!(slugInput(slug) && (probe?.primary || probe?.secondary));

  /* ⚠️⚠️ SLUG NE ZAMAN GÖNDERİLİR — üç durum ayrı.
        · DOKUNULMAMIŞ otomatik tahmin  → GÖNDERİLMEZ. Uydurma veridir;
          kaynakta o sayfa yoksa senkron her turda boşa arar ve eşya
          `price_sync_missing` olur. (Aynı sınıf: `value_seen: d.value || 1`)
        · Operatör DEĞİŞTİRDİ            → GÖNDERİLİR. Doğrulamamış olsa bile
          bu onun AÇIK niyeti; sessizce yutmak, ekranın "This name is saved
          with the item" vaadini yalan yapardı.
        · Kaynakta DOĞRULANDI            → GÖNDERİLİR.
        ⚠️ Karşılaştırma dokunulmamış TAHMİNE göre, kataloğa göre değil:
           kutu mevcut slug'la açılıyor ve o hâliyle gönderilmesi zararsız
           (aynı değerin üzerine yazılır). */
  const slugNow = slugInput(slug);

  /* ⚠️⚠️ YAZILAN SLUG MEVCUT BİR EŞYAYA AİT Mİ — KAYDETMEDEN ÖNCE SÖYLE.
        🔴 Kapatılan tıkanma: operatör kaynağın adını (“clownfish-gun”)
        yazıyor, kaydediyor ve sunucudan “Name/alias collides with
        'clownfish_gun'” alıyordu. Sunucu HAKLI — iki eşya aynı anahtarı
        sahiplenemez — ama operatör ne yapacağını bilmiyordu.
        Gerçek durum şu: bu deposit muhtemelen O MEVCUT EŞYANIN kendisi;
        yeni kayıt açılmamalı, botun bildirdiği ad o eşyaya TAKMA AD olarak
        eklenmeli (ya da assetId ona bağlanmalı).
        Panelde katalog dizini zaten var; 400'ü beklemeden söyleyebiliriz. */
  const slugOwner = useMemo(() => {
    const k = normKey(slugNow);
    if (!k || !index) return null;
    const hit = (index.get(k) || [])[0];
    return (hit && hit.id !== found.item?.id) ? hit : null;
  }, [slugNow, index, found]);
  /* ⚠️ Karar "operatör DOKUNDU mu" üzerinden verilir — ham adla karşılaştırma
        üzerinden DEĞİL. Karşılaştırma, katalogdan senkronlanan mevcut slug'ı
        da "değişmiş" sayıp gereksiz yere geri yazardı; ayrıca operatörün
        tahmini AYNEN onaylamasını "dokunmadı" sanırdı. */
  const slugSend = !!slugNow && (slugVerified || slugTouched);

  const save = async () => {
    const value = Number(effValue);
    if (!Number.isInteger(value) || value < 1 || value > 100_000_000) {
      return toast.error('Check the sources first, or type a value as a last resort.');
    }
    if (highValue && !confirmHigh) {
      return toast.error(`This prices the item at ${num(value)}. Tick the confirm box if the name is right.`);
    }
    /* ⚠️ `asText`: bozuk `id`/`name` (sayı, sözlük) `.trim()`te çökertiyordu.
          Ayrıca sayı `id` regex testinden geçip sunucuya gidiyor ve 422
          alıyordu — sessiz bir çıkmaz. */
    const id = asText(existing?.id) || slugId(rawName);
    if (!/^[a-z0-9_]{2,48}$/.test(id)) {
      return toast.error('Cannot derive a valid item ID from this name.');
    }
    setBusy('save');
    try {
      await axios.post(`${API}/admin/mm2/items/upsert`, {
        id,
        name: (asText(existing?.name) || rawName).trim().slice(0, 64),
        image_url: imageUrl,
        value,
        rarity,
        active: true,
        aliases: aliasList,
        /* Fiyat az önce belirlendi; damga olmadan eşya `never_priced` kalır
           ve bakiye rayında işlem göremez (bkz. _mm2_price_fresh).
           ⚠️ Sunucu damgayı OWNER'a bağladı — bakiye rayının kapısı olduğu
              için. Admin gönderirse 403 döner ve kayıt HİÇ yapılmaz, oysa
              kayıt kendisi adminin hakkı. Bu yüzden rütbeye göre gönderiyoruz
              ve admin'e ne olacağını aşağıda yazıyoruz. */
        stamp_price: true,
        /* ⚠️⚠️ SLUG ARTIK GERÇEKTEN KAYDEDİLİYOR.
           🔴 Kapatılan açık: bu alan gönderilmiyordu ve sunucu modeli de
           kabul etmiyordu; slug yalnızca fiyat SORGUSU için kullanılıp
           atılıyordu. Alttaki mesaj yine de "Future syncs will use …"
           diyordu — yani EKRAN YALAN SÖYLÜYORDU. Eşya bir sonraki senkronda
           ADA göre eşleşmeye çalışıyor, ad kaynakla tutmadığı için
           (operatörün slug yazmasının sebebi TAM DA BU) bayatlıyor ve
           Price Issues'a düşüyordu: ekran, çözdüğü sorunu geri üretiyordu.
           ⚠️ Sunucu bunu §5.4 çakışma zırhından geçirir; başka bir eşyanın
              anahtarıyla çakışırsa 400 döner ve HİÇBİR ŞEY yazılmaz. */
        ...(slugSend ? { price_slug: slugNow } : {}),
        /* ⚠️⚠️ GERÇEK assetId KATALOĞA BAĞLANIR — ASIL ÇÖZÜM BU.
           Bu kuyruğun var olma sebebi ad belirsizliği (aynı adı taşıyan
           birden fazla eşya). Yalnızca fiyat yazmak eşyayı bu SEFER kurtarır
           ama AYNI eşya bir daha yatırıldığında yine ada bakılır ve yine
           buraya düşer. assetId bağlanınca eşleşme ADA HİÇ BAKMADAN
           doğrudan yapılır ve sorun KALICI olarak kapanır.
           ⚠️ `existing` varsa mevcut assetId'lere EKLENİR, üzerine yazılmaz:
              aynı eşyanın birden çok Roblox varyantı olabilir.
           ⚠️ Sunucu §1 zırhını uygular: assetId başka bir eşyada duruyorsa
              400 döner. Bu doğru davranış — o durumda operatörün önce
              oradaki yanlış bağı temizlemesi gerekir. */
        /* ⚠️⚠️ category / kind / game_name GÖNDERİLMEZSE MODEL VARSAYILANI
           EZER. Ölçüldü: `pets`→`weapons`, `knife`→`other`. Bu ekran VARYANT
           belirleme ekranı; kaydederken varyant bilgisini SİLMESİ tam tersi
           bir sonuç. Sıra: mevcut katalog kaydı → botun bildirdiği kategori
           → güvenli varsayılan.
           ⚠️ `kind` için botun bildirdiği bir karşılık yok; mevcut değer
              korunur, yoksa `other` kalır (operatör katalog ekranından
              düzeltir — uydurmuyoruz). */
        category: (existing?.category === 'pets' || existing?.category === 'weapons')
          ? existing.category
          : (rowDetail?.category === 'pets' ? 'pets' : 'weapons'),
        kind: ['gun', 'knife', 'pet', 'other'].includes(existing?.kind)
          ? existing.kind : 'other',
        game_name: (typeof existing?.game_name === 'string' && existing.game_name.trim())
          ? existing.game_name
          : (asText(existing?.name) || rawName).trim().slice(0, 64),
        /* ⚠️ `no_icon` KORUNUR. Sabit `false` göndermek, "bu eşyanın
           bilerek assetId'si yok" işaretini silerdi. AssetId bağlıyorsak
           zaten false olmalı (sunucu ikisini birlikte reddeder). */
        no_icon: rowAsset ? false : !!existing?.no_icon,
        /* ⚠️ `asIdList`: bozuk `icon_ids` (sözlük) yayılırken çökertiyordu —
              ve bu, §5.7'de ÜRETİMDE GÖRÜLMÜŞ bir durum. Ayrıca sayı olarak
              duran assetId'ler metne çevrilir; sunucu metin bekliyor. */
        icon_ids: rowAsset
          ? Array.from(new Set([...asIdList(existing?.icon_ids), rowAsset]))
          : asIdList(existing?.icon_ids),
      }, auth);
      /* ⚠️ MESAJ NE YAPILDIYSA ONU SÖYLER. İlk yazımda ekran "Future syncs
         will use '<slug>'" diyordu ama slug hiç kaydedilmiyordu — operatör
         işi bitti sanıp gidiyordu. Artık üç durum AYRI: */
      toast.success([
        `Saved at ${num(value)}`,
        rowAsset ? `asset ${rowAsset} bound — future deposits match directly` : null,
        slugSend ? `source “${slugNow}” saved${slugVerified ? "" : " (unverified)"}` : null,
      ].filter(Boolean).join(' · ') + '.', { duration: 7000 });
      if (!slugSend && !rowAsset) {
        /* ⚠️ Ne assetId ne doğrulanmış slug varsa eşleşme YALNIZCA ADA kalır
           — bu eşyanın buraya düşme sebebi de tam olarak oydu. Sessiz
           kalmak, operatörü sorunun çözüldüğüne inandırırdı. */
        toast('No asset id and no verified source name — matching still relies on '
              + 'the item name alone, so this may land back in this queue.',
              { duration: 9000 });
      }
      await onCatalogChanged();
    } catch (err) {
      toast.error(errText(err, 'Could not save the item.'), { duration: 9000 });
    } finally { setBusy(''); }
  };

  const grant = async () => {
    const id = found.item?.id;
    if (!id) return;
    /* ⚠️ KONUM YOKSA HİÇ DENEME. Sunucu reddedecek zaten, ama hatayı burada
       söylemek operatöre ne yapması gerektiğini de söylüyor: kaydın botu
       bilinmiyorsa eşya elle bulunmalı, tahmin edilmemeli. */
    if (!record.bot_id) {
      return toast.error(
        'This record does not say which bot holds the item, and guessing would ' +
        'make it impossible to withdraw. Find the trade in Audit Logs, then grant ' +
        'it with the right bot from the API.', { duration: 12000 });
    }
    setBusy('grant');
    try {
      await axios.post(`${API}/admin/mm2/inventory/grant`, {
        user_id: target, item_id: id, count: 1,
        /* ⚠️ Kuyruk kaydındaki bot, yatırmayı fiziksel olarak ALAN bottur.
           Göndermezsek sunucu "en boş" botu seçer — ki o çoğu zaman hiç
           eşya tutmayan, linki bile girilmemiş bottur. */
        /* ⚠️ Kuyruk kaydındaki bot, yatırmayı FİZİKSEL OLARAK alan bottur.
           Çekim bacakları bot başına bölündüğü için yanlış bota yazılan
           eşyayı o bot veremez ve çekim başarısız olur. Sunucu boş bot_id'yi
           artık reddediyor — burada da hiç göndermiyoruz. */
        bot_id: record.bot_id,
        synthetic: false,   // eşya gerçek ve o botta duruyor
        reason: `pending deposit ${String(record.trade_id || record.id).slice(0, 8)}`,
      }, auth);
      toast.success(toHouse ? `Granted ${asText(found.item.name) || asText(found.item.id)} to the house.`
                            : `Granted ${asText(found.item.name) || asText(found.item.id)} to ${asText(record.username) || target}.`);
      onGranted(rowKey);
    } catch (err) {
      toast.error(errText(err, 'Could not grant the item.'), { duration: 9000 });
    } finally { setBusy(''); }
  };

  const needsPricing = found.status === 'new' || found.status === 'disabled';
  const blocked = found.status === 'collision' || found.status === 'unmatchable';

  return (
    <div className={`rounded-xl border ${granted ? 'border-emerald-500/25 bg-emerald-500/[0.04]' : 'border-[#2A2D31] bg-[#0B0C0E]'}`}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        {/* ⚠️ SATIR KAPALIYKEN DE FOTOĞRAF. Bu kuyruğa düşmenin sebebi ad
            belirsizliği; operatörün "hangi varyant?" sorusuna cevabı satırı
            AÇMADAN görmesi gerekir. Resim yoksa eski Package ikonu kalır. */}
        {assetThumb ? (
          <img alt="" src={safeSrc(assetThumb)}
            className="w-8 h-8 rounded-lg bg-[#121417] border border-[#2A2D31] object-contain shrink-0"
            onError={e => { e.currentTarget.style.opacity = '0.15'; }}/>
        ) : (
          <Package className={`w-4 h-4 shrink-0 ${granted ? 'text-emerald-400' : 'text-zinc-600'}`}/>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-white truncate">{asText(rawName) || String(rawName ?? '')}</p>
          {/* ⚠️ assetId GÖRÜNÜR OLMALI: aynı adı taşıyan iki kaydı ayıran tek
              şey odur, ve operatör bunu Roblox'ta arayabilmelidir. */}
          <p className="text-[10px] text-zinc-600 font-mono truncate">
            matches on: {found.key || '—'}
            {rowAsset && <span className="text-sky-400/70"> · asset {rowAsset}</span>}
          </p>
        </div>
        {granted ? (
          <span className="text-[10px] font-bold px-2 py-1 rounded-full border text-emerald-300 bg-emerald-500/10 border-emerald-500/30 shrink-0">GRANTED</span>
        ) : (
          <span className={`text-[10px] font-bold px-2 py-1 rounded-full border shrink-0 ${cfg.cls}`}>{cfg.label}</span>
        )}
        <button onClick={() => setOpen(o => !o)}
          className="p-1.5 rounded-lg text-zinc-600 hover:text-white hover:bg-white/5 transition-colors shrink-0"
          aria-label={open ? 'Collapse' : 'Expand'}>
          {open ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} className="overflow-hidden border-t border-[#2A2D31]">
            <div className="p-3 space-y-3">

              {found.status === 'collision' && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-500/30 bg-red-500/10">
                  <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5"/>
                  <p className="text-xs text-red-300 leading-relaxed">
                    <span className="font-bold">{found.matches.map(m => m.id).join(' and ')} both normalise to “{found.key}”.</span>{' '}
                    The catalog drops colliding keys entirely, so deposits using this name never credit.
                    Rename one of them in the catalog first, then come back.
                  </p>
                </div>
              )}

              {found.status === 'unmatchable' && (
                <p className="text-xs text-red-300 leading-relaxed px-3 py-2 rounded-lg border border-red-500/30 bg-red-500/10">
                  This name has no letters or digits, so it can never be matched safely. Grant the
                  correct item by hand from the catalog and close the record with a note.
                </p>
              )}

              {found.status === 'disabled' && (
                <p className="text-xs text-orange-300 leading-relaxed px-3 py-2 rounded-lg border border-orange-500/30 bg-orange-500/10">
                  <span className="font-bold">{asText(found.item.id) || '(id yok)'} exists but the catalog skips it</span>
                  {found.item.active === false ? ' — it is inactive.' : ` — its value is ${num(found.item.value)}.`}
                  {' '}Look the price up below; saving re-activates it. Its existing aliases and image are kept.
                </p>
              )}

              {found.status === 'ready' && (
                <p className="text-xs text-emerald-300 leading-relaxed px-3 py-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10">
                  Already priced at <span className="font-bold">{num(found.item.value)}</span> as
                  “{asText(found.item.name) || asText(found.item.id)}”. Nothing to look up — just grant it.
                </p>
              )}

              {needsPricing && !blocked && (
                <>
                  {/* ── 1. adım: doğru arama adı ─────────────────────── */}
                  <div className="space-y-2">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">
                      1 · Name used on the price sites
                    </p>
                    <p className="text-[11px] text-zinc-500 leading-relaxed">
                      Open a source, find the item, and copy the last part of its URL.
                      This name is saved with the item, so every future sync looks it up the same way.
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div>
                        <input value={slug} onChange={e => { setSlug(e.target.value); setSlugTouched(true); setProbe(null); }}
                          className={inputCls} placeholder="deepsea"/>
                        {/* ⚠️ ALANIN NE YAPACAĞI EKRANDA YAZAR.
                            Üstteki metin "This name is saved with the item"
                            diye SÖZ veriyor; dokunulmamış otomatik tahmin
                            KAYDEDİLMEDİĞİ için o söz her durumda doğru
                            değildi. Sessiz kalmak, operatörü işin bittiğine
                            inandırırdı — bu ekranın kapattığı hata sınıfının
                            ta kendisi. */}
                        {slugOwner && (
                          <p className="text-[10px] mt-1 leading-snug text-amber-300">
                            This name already belongs to <span className="font-mono font-bold">{asText(slugOwner.id)}</span>
                            {' '}(&ldquo;{asText(slugOwner.name)}&rdquo;). Saving here would create a second
                            item claiming the same key, and the server will reject it.
                            {' '}<span className="font-bold">This deposit is probably that item</span> — open
                            MM2 Item Identity, add &ldquo;{asText(rawName) || String(rawName ?? '')}&rdquo; to its
                            aliases{rowAsset ? <> or bind asset <span className="font-mono">{rowAsset}</span> to it</> : null},
                            then refresh here and use Grant.
                          </p>
                        )}
                        <p className={`text-[10px] mt-1 leading-snug ${
                          slugVerified ? 'text-emerald-400/80'
                          : slugSend ? 'text-amber-300/80' : 'text-zinc-600'}`}>
                          {slugVerified
                            ? <>Verified on a source — will be saved as “{slugNow}”.</>
                            : slugSend
                              ? <>Will be saved as “{slugNow}” <span className="text-zinc-500">
                                  (not verified — check the sources to be sure)</span></>
                              : <>{found.item?.price_slug
                                    ? <>Already saved with the item — <span className="font-bold">unchanged</span>.</>
                                    : <>Auto-guess from the item name — <span className="font-bold">not saved</span>.
                                       Check the sources, or edit it, to store it with the item.</>}</>}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          {SOURCES.map(s => (
                            <a key={s.label} href={s.url(slugInput(slug))} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[10px] font-bold text-zinc-500 hover:text-white transition-colors">
                              {s.label} <ExternalLink className="w-2.5 h-2.5"/>
                            </a>
                          ))}
                        </div>
                      </div>
                      <div>
                        <input value={slug2} onChange={e => { setSlug2(e.target.value); setProbe(null); }}
                          className={inputCls} placeholder="second source name (only if different)"/>
                        <p className="text-[10px] text-zinc-600 mt-1.5">Leave empty to use the same name on both.</p>
                      </div>
                    </div>

                    <button onClick={probeSources} disabled={!canLookup || busy === 'probe'}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#121417] border border-[#2A2D31] text-xs font-bold text-zinc-300 hover:text-white hover:border-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      {busy === 'probe' ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Search className="w-3.5 h-3.5"/>}
                      Check sources
                    </button>
                  </div>

                  {/* ── 2. adım: kaynakların dediği ───────────────────── */}
                  {probe && (
                    <div className={`rounded-lg border px-3 py-2.5 space-y-2 ${
                      probe.auto_ok ? 'border-emerald-500/30 bg-emerald-500/10'
                                    : 'border-amber-500/30 bg-amber-500/10'}`}>
                      <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">
                        2 · What the sources say
                      </p>
                      <div className="grid gap-1.5 sm:grid-cols-2">
                        <SrcLine label={probe.primary?.source || 'primary'} v={probe.primary?.value}/>
                        <SrcLine label={probe.secondary?.source || 'secondary'} v={probe.secondary?.value}/>
                      </div>
                      <p className={`text-xs leading-relaxed ${probe.auto_ok ? 'text-emerald-300' : 'text-amber-300'}`}>
                        {probe.auto_ok
                          ? <>Storing <span className="font-bold">{num(probe.value)}</span> — {probe.reason}.</>
                          : <>The automatic path would refuse this: {probe.reason}. You can still save it,
                             but check the name is right before you do.</>}
                        {scaled !== null && <span className="text-zinc-400"> Players see {num(scaled)}.</span>}
                      </p>
                      {probe.from_index && (
                        <p className="text-[10px] text-zinc-500">Read from the last sync snapshot — no request needed.</p>
                      )}
                      {/* ⚠️ Zaman aşımı "listelenmiyor" ile AYNI ŞEY DEĞİL.
                          Ayırmazsak operatör kaynağı yok sanıp elle değer
                          girer — ölçüm hatası insan hatasına dönüşür. */}
                      {probe.timed_out && (
                        <p className="text-[11px] text-amber-300 leading-relaxed">
                          <span className="font-bold">A source did not answer in time.</span>{' '}
                          “not listed” above may just be a slow site — try again before
                          treating this as a single-source item.
                        </p>
                      )}
                    </div>
                  )}

                  {/* görsel */}
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Field label="Image name" hint="only if it differs from the name above">
                      <input value={imgSlug} onChange={e => setImgSlug(e.target.value)}
                        className={inputCls} placeholder={slugInput(slug) || 'deepsea'}/>
                    </Field>
                    <div className="flex items-end gap-2 pb-1">
                      {/* ⚠️ ÖNİZLEME `previewUrl` — kaydedilecek `imageUrl` DEĞİL.
                          Botun getirdiği GERÇEK eşyanın resmi varsa (assetId
                          thumb'ı) o gösterilir: operatörün "hangi varyant
                          geldi?" sorusunu cevaplayan tek güvenilir şey odur.
                          Kataloğa yazılan adres ayrı tutulur ki yerel kopya
                          (mm2_download_images.py) ezilmesin. Fark varsa
                          aşağıda AÇIKÇA yazılır — sessiz ayrışma olmaz. */}
                      {previewUrl ? (
                        <>
                          <img alt="" src={previewUrl}
                            className="w-10 h-10 rounded-lg bg-[#121417] border border-[#2A2D31] object-contain"
                            onError={e => { e.currentTarget.style.opacity = '0.15'; }}/>
                          <p className="text-[10px] text-zinc-600 leading-snug">
                            {assetThumb
                              ? <>Real photo of the deposited item (asset {rowAsset}).{' '}
                                  {imageUrl && imageUrl !== assetThumb
                                    ? 'The catalog keeps its own image — this preview is only to identify the variant.'
                                    : ''}</>
                              : (existing?.image_url && !slugInput(imgSlug)
                                  ? 'Keeping the current image. Type a name above to replace it.'
                                  : 'A blank box means the name is wrong — the item still saves, the panel just draws initials.')}
                          </p>
                        </>
                      ) : (
                        <p className="text-[10px] text-zinc-600">Check sources to preview the image.</p>
                      )}
                    </div>
                  </div>

                  {/* son çare: elle değer */}
                  {probe && !probe.primary && !probe.secondary && (
                    <Field label="Value (last resort — neither source lists this item)"
                      hint="raw source number; only use it when you have verified the price yourself">
                      <input value={manual} inputMode="numeric"
                        onChange={e => setManual(e.target.value.replace(/[^\d]/g, ''))}
                        className={inputCls} placeholder="e.g. 1200"/>
                    </Field>
                  )}

                  {/* aliaslar — kalıcı eşlemeyi taşıyan alan */}
                  {aliasList.length > 0 && (
                    <p className="flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed">
                      <Link2 className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
                      <span>
                        Saved with aliases <span className="text-zinc-300 font-mono">{aliasList.join(', ')}</span> —
                        that is what makes every later sync find this item.
                        {aliasBuild.dropped > 0 && (
                          <span className="text-amber-300 font-bold">
                            {' '}This item already has 20 aliases, the server limit,
                            so {aliasBuild.dropped} new one(s) will not be added.
                            Remove an unused alias from the catalog first.
                          </span>
                        )}
                      </span>
                    </p>
                  )}

                  {highValue && (
                    <label className="flex items-start gap-2 text-[11px] text-amber-300 leading-relaxed cursor-pointer">
                      <input type="checkbox" checked={confirmHigh} onChange={e => setConfirmHigh(e.target.checked)}
                        className="mt-0.5 accent-amber-400"/>
                      This prices the item at {num(effValue)}. I checked the source page and it is the right item.
                    </label>
                  )}
                </>
              )}

              {/* eylemler */}
              {!blocked && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {needsPricing && (
                    <button onClick={save} disabled={!canWrite || busy === 'save' || effValue === null}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#121417] border border-[#2A2D31] text-xs font-bold text-zinc-300 hover:text-white hover:border-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>}
                      {existing ? 'Update catalog entry' : 'Save to catalog'}
                    </button>
                  )}

                  <button onClick={grant}
                    disabled={!canGrant || !record.bot_id || found.status !== 'ready' || granted || busy === 'grant'}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-xs font-bold text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    {busy === 'grant' ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Gift className="w-3.5 h-3.5"/>}
                    {granted ? 'Granted' : toHouse ? 'Grant to house' : `Grant to ${asText(record.username) || 'user'}`}
                  </button>

                  {!canLookup && <span className="text-[11px] text-zinc-600">Admin rank required to look prices up.</span>}
                  {canLookup && !canWrite && <span className="text-[11px] text-zinc-600">
                    Owner rank required to save prices and grant items. Find the right name here,
                    then hand it over.</span>}
                  {/* ⚠️ KAPALI BUTON NEDENİNİ SÖYLEMELİ. Sebepsiz kapalı bir
                      buton, kullanıcıyı yetkisi olduğu hâlde çıkmaza sokar —
                      tam olarak bu sayfanın çözmeye çalıştığı sorunun küçük
                      hâli. */}
                  {/* ⚠️ ADMIN fiyat yazabilir ama eşya VEREMEZ; sebebi
                         yazılmazsa düğmeyi bozuk sanır. */}
                  {canWrite && !canGrant && found.status === 'ready' &&
                    <span className="text-[11px] text-zinc-600">
                      Writing prices and granting items both need owner rank — look the
                      value up here, then ask an owner to save and hand it over.
                    </span>}
                  {canWrite && !record.bot_id &&
                    <span className="text-[11px] text-red-300">Cannot grant: this record has no bot location.</span>}
                  {canWrite && needsPricing && effValue === null &&
                    <span className="text-[11px] text-zinc-600">Run “Check sources” first — the value comes from there, not from you.</span>}
                  {canWrite && needsPricing && effValue !== null && found.status !== 'ready' && !granted &&
                    <span className="text-[11px] text-zinc-600">Save the item, then grant it.</span>}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SrcLine = ({ label, v }) => (
  <div className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-black/20">
    <span className="text-[11px] text-zinc-400 truncate">{label}</span>
    <span className={`text-[11px] font-bold shrink-0 ${v == null ? 'text-zinc-600' : 'text-white'}`}>
      {v == null ? 'not listed' : num(v)}
    </span>
  </div>
);

const inputCls = 'w-full px-2.5 py-2 rounded-lg bg-[#121417] border border-[#2A2D31] text-xs text-white ' +
  'placeholder:text-zinc-700 focus:outline-none focus:border-zinc-500 disabled:opacity-50';

const Field = ({ label, hint, children }) => (
  <div className="min-w-0">
    <label className="block text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1">{label}</label>
    {children}
    {hint && <p className="text-[10px] text-zinc-600 mt-1 leading-snug">{hint}</p>}
  </div>
);

/* ════════════════════════════════════════════════════════════
   KUYRUK KAYDI
   ════════════════════════════════════════════════════════════ */
const PendingRecord = ({ record, index, multiplier, viewerRank, onResolved, onCatalogChanged }) => {
  const { token, user } = useAuth();
  const [granted, setGranted] = useState(() => new Set());
  const [closing, setClosing] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  /* ⚠️⚠️ `names` DİZİ OLMAYABİLİR.
        🔴 Ölçülen çökme (gerçek render fuzz'ı): `names` sözlük/metinse
        aşağıdaki `names.map(...)` TypeError atıyor ve bu kaydın TAMAMI —
        yani kullanıcının borcu — ekrandan siliniyor. Sunucu bugün liste
        yazıyor ama bu ekranın dayanağı olamaz: belge veritabanından geliyor.
        ⚠️ Elemanlar da metne zorlanır: dizide sözlük varsa `rawName` olarak
           React'e gider. */
  const names = asArray(record.names)
    .map(x => (typeof x === 'string' ? x : String(x ?? '')))
    .filter(Boolean);

  /* ⚠️⚠️ GÖRSELLER KAYIT BAŞINA TEK İSTEKTE — satır başına DEĞİL.
        Bir yatırma 30 eşyaya kadar taşıyabilir; satır başına istek atmak
        30 çağrı demekti. Sunucu modeli (`MM2ThumbReq`) zaten 100'lük toplu
        istek için yazılmış.
        ⚠️ Yanıt anahtarı `urls` — `thumbs` DEĞİL. (İlk yazımda `thumbs`
           okunuyordu ve görsel HİÇ yüklenmiyordu; uç
           `{"urls": {...}, "failed": [...]}` döndürüyor.)
        ⚠️ Görsel KOZMETİKTİR: hata akışı durdurmaz, satırlar yine çalışır. */
  const [thumbs, setThumbs] = useState({});

  /* ⚠️ BAĞIMLILIK NESNE DEĞİL, KARARLI BİR ANAHTAR.
        🔴 Kapatılan israf: bağımlılık `record` nesnesiydi. Sayfa 15 saniyede
        bir yenileniyor ve her yenilemede YENİ nesne referansı geliyor —
        içerik aynı olsa bile effect yeniden çalışıp her kayıt için görsel
        isteği atıyordu. Ekranda 20 kayıt varsa dakikada 80 gereksiz çağrı.
        assetId listesi metne çevrilince, içerik GERÇEKTEN değişmedikçe
        effect yeniden çalışmaz. */
  const assetKey = useMemo(() => Array.from(new Set(
    (Array.isArray(record?.detail) ? record.detail : [])
      .map(d => (d && typeof d === 'object' ? String(d.asset ?? '') : ''))
      .filter(a => /^[0-9]{1,20}$/.test(a))
  )).sort().slice(0, 100).join(','), [record]);

  useEffect(() => {
    const ids = assetKey ? assetKey.split(',') : [];
    if (!ids.length) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.post(`${API}/admin/mm2/asset-thumbs`,
          { asset_ids: ids },
          { headers: { Authorization: `Bearer ${token}` } });
        const u = r?.data?.urls;
        if (!alive || !u || typeof u !== 'object') return;
        const clean = {};
        for (const [k, v] of Object.entries(u)) {
          /* Sunucu zaten https + uzunluk doğruluyor; bileşen de bağımsız
             dayanıklı olsun (§5.7: panel kendi başına ayakta kalmalı). */
          if (typeof v === 'string' && v.startsWith('https://')) clean[k] = v;
        }
        setThumbs(clean);
      } catch { /* sessiz: görsel yoksa satır ada göre çalışmaya devam eder */ }
    })();
    return () => { alive = false; };
  }, [assetKey, token]);

  /* ⚠️ `target` hem EKRANA çiziliyor hem de /inventory/grant gövdesinde
        SUNUCUYA gidiyor. Nesne olursa React çöker, sunucu da 422 verir. */
  const target = asText(record.grant_to) || asText(record.user_id);
  const toHouse = target === '__mm2_house__';
  const allGranted = names.length > 0 && names.every((_, i) => granted.has(i));
  /* ⚠️ Kaydı kapatmak sunucuda OWNER (pending-items/resolve). Eşik 4 iken
        ADMIN düğmeyi açık görüp 403 alıyordu — bkz. `canGrant` notu. */
  const canResolve = resolveRank(viewerRank, user) >= 4;  // resolve · OWNER(4)

  const resolve = async () => {
    if (note.trim().length < 3) return toast.error('Write a short note — it goes into the audit trail.');
    setBusy(true);
    try {
      await axios.post(`${API}/admin/mm2/pending-items/resolve`,
        { pending_id: record.id, note: note.trim().slice(0, 200) },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Record closed.');
      setClosing(false);
      onResolved();
    } catch (err) {
      toast.error(errText(err, 'Could not close the record.'), { duration: 9000 });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#2A2D31] bg-[#121417] overflow-hidden">
      {/* üst bilgi */}
      <div className="px-4 py-3 border-b border-[#2A2D31] flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate">
            {asText(record.username) || asText(record.user_id) || '(bilinmiyor)'}
            {asText(record.roblox_username) && <span className="text-zinc-500 font-normal"> · {asText(record.roblox_username)}</span>}
          </p>
          <p className="text-[10px] text-zinc-600 font-mono truncate">
            trade {String(record.trade_id || '').slice(0, 8)} · {relTime(record.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-[10px] font-bold px-2 py-1 rounded-full border border-[#2A2D31] text-zinc-400">
            {names.length} item{names.length === 1 ? '' : 's'}
          </span>
          <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${
            record.mode === 'balance' ? 'text-sky-300 bg-sky-500/10 border-sky-500/30'
            : record.mode === 'house' ? 'text-amber-300 bg-amber-500/10 border-amber-500/30'
            : 'text-zinc-400 border-[#2A2D31]'}`}>
            {String(record.mode || 'items').toUpperCase()}
          </span>
        </div>
      </div>

      {/* hedef + mod uyarıları */}
      <div className="px-4 pt-3 space-y-2">
        <p className="text-[11px] text-zinc-500">
          Grants go to{' '}
          <span className={`font-bold ${toHouse ? 'text-amber-300' : 'text-white'}`}>
            {toHouse ? 'the house' : (asText(record.username) || asText(target) || '(bilinmiyor)')}
          </span>
          <span className="text-zinc-600 font-mono"> ({String(target).slice(0, 20)})</span>
          {/* ⚠️ Eşya FİZİKSEL OLARAK bu botta. Konumu deftere doğru yazmazsak
              çekim istendiğinde bot elinde olmayan bir şeyi vermeye çalışır. */}
          {record.bot_id
            ? <> · held on bot <span className="text-zinc-300 font-mono">{asText(record.bot_id)}</span>
                {record.bot_id_recovered && <span className="text-zinc-600"> (read from the trade)</span>}</>
            : <span className="text-red-300 font-bold"> · bot unknown — granting is blocked,
                because filing the item under the wrong bot makes it unwithdrawable</span>}
        </p>

        {/* ⚠️⚠️ BAKİYE MODUNDA GRANT, YATIRMANIN YAPACAĞI ŞEY DEĞİLDİR.
              Başarılı bir bakiye yatırması eşyayı KASAYA basar
              (user_id=house, state=house) ve kullanıcıya PARA öder.
              Buradaki grant ise eşyayı kullanıcının envanterine koyar.
              🔴 Önceki metin "mağazada satabilir" diyordu — YANLIŞ.
                 /game/mm2/shop/withdraw bakiye→eşya yönünde çalışıyor;
                 eşya→bakiye diye bir uç YOK. Yanlış bilgi, operatörün
                 kullanıcıya yanlış söz vermesine yol açardı. */}
        {record.mode === 'balance' && (
          <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg border border-sky-500/30 bg-sky-500/10">
            <Info className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5"/>
            <div className="text-[11px] text-sky-300 leading-relaxed space-y-1.5">
              <p><span className="font-bold">This was a balance deposit — they wanted credit, not the item.</span>{' '}
                There is no way for a user to turn an inventory item back into balance,
                so granting it to them does not finish what they started.</p>
              <p className="text-zinc-400">
                To complete it as a balance deposit: grant to <span className="font-mono text-zinc-300">__mm2_house__</span>{' '}
                on the bot below, then credit the balance yourself under User Lookup →
                balance adjust. Otherwise grant the item and tell them they will need to
                withdraw and re-deposit it.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* eşyalar */}
      <div className="p-4 pt-3 space-y-2">
        {names.map((n, i) => (
          <ItemRow key={`${record.id}:${i}`} rawName={n} rowKey={i} record={record}
            index={index} multiplier={multiplier} viewerRank={viewerRank}
            granted={granted.has(i)}
            onGranted={(k) => setGranted(s => new Set(s).add(k))}
            onCatalogChanged={onCatalogChanged} thumbs={thumbs}/>
        ))}
      </div>

      {/* kapatma */}
      <div className="px-4 py-3 border-t border-[#2A2D31] flex flex-wrap items-center gap-2">
        {!closing ? (
          <>
            <button onClick={() => setClosing(true)} disabled={!canResolve}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0B0C0E] border border-[#2A2D31] text-xs font-bold text-zinc-400 hover:text-white hover:border-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <Check className="w-3.5 h-3.5"/> Close record
            </button>
            <span className="text-[11px] text-zinc-600">
              {allGranted ? 'All items granted in this session.'
                : `${granted.size}/${names.length} granted here.`}
            </span>
          </>
        ) : (
          <div className="w-full space-y-2">
            {/* ⚠️ Bu uç EŞYA VERMEZ. Onay metni bunu açıkça söylüyor:
                  "kapat" tuşuna borç ödendi sanarak basmak, kullanıcının
                  eşyasını izsiz silmek olurdu. */}
            <p className="text-xs text-amber-300 leading-relaxed">
              Closing only marks the record handled — it does not give anything.
              {' '}Grants are not tracked across page reloads, so check the user’s
              inventory if you are unsure whether an item already went out.
              {!allGranted && <span className="font-bold"> {names.length - granted.size} item(s)
                have not been granted in this session. Only close if you granted them another way.</span>}
            </p>
            <input value={note} onChange={e => setNote(e.target.value)} maxLength={200}
              placeholder="What did you do? e.g. priced from mm2values, granted 3 items"
              className={inputCls}/>
            <div className="flex items-center gap-2">
              <button onClick={resolve} disabled={busy}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/15 border border-red-500/30 text-xs font-bold text-red-300 hover:bg-red-500/25 disabled:opacity-40 transition-colors">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Check className="w-3.5 h-3.5"/>}
                Close record
              </button>
              <button onClick={() => { setClosing(false); setNote(''); }}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2A2D31] text-xs font-bold text-zinc-500 hover:text-white transition-colors">
                <X className="w-3.5 h-3.5"/> Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   SAYFA
   ════════════════════════════════════════════════════════════ */
export const Mm2pendingitemreviews = ({ viewerRank }) => {
  const { token, user } = useAuth();
  const rank = resolveRank(viewerRank, user);
  const [records, setRecords] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [multiplier, setMultiplier] = useState(null);
  const [loading, setLoading] = useState(true);

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadCatalog = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/mm2/items/all`, auth);
      setCatalog(r.data?.items || []);
    } catch { /* dizin boş kalır: satırlar "not in catalog" gösterir, yanlış değil */ }
  }, [auth]);

  const load = useCallback(async () => {
    try {
      const [p] = await Promise.all([
        axios.get(`${API}/admin/mm2/pending-items`, auth),
        loadCatalog(),
      ]);
      setRecords(p.data?.pending || []);
    } catch (err) {
      toast.error(errText(err, 'Could not load the queue.'));
    } finally { setLoading(false); }
  }, [auth, loadCatalog]);

  /* Çarpanı sunucudan al — sabit yazmak, .env değiştiğinde arayüzün sessizce
     yanlış önizleme göstermesi demekti. */
  useEffect(() => {
    axios.get(`${API}/game/mm2/config`)
      .then(r => setMultiplier(r.data?.balance_rail?.value_multiplier ?? null))
      .catch(() => setMultiplier(null));
  }, []);

  useEffect(() => { if (token) load(); }, [token, load]);

  const index = useMemo(() => buildIndex(catalog), [catalog]);
  const totalItems = records.reduce((n, r) => n + (r.names?.length || 0), 0);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="space-y-4">

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-amber-400"/> Pending Items
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Deposits the bot received but could not price — the user is owed these.
          </p>
        </div>
        <button onClick={load}
          className="px-3 py-2 rounded-lg bg-[#121417] border border-[#2A2D31] text-xs font-bold text-zinc-400 hover:text-white flex items-center gap-2 transition-colors">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`}/> Refresh
        </button>
      </div>

      <SyncStrip token={token}/>

      {rank < 4 && (
        <p className="text-[11px] text-zinc-500 bg-[#121417] border border-[#2A2D31] rounded-lg px-3 py-2">
          {rank >= 3
            ? 'You can look prices up on the sources. Saving them, granting items and closing records need owner rank — item values decide how much a deposit pays out.'
            : 'View only — admin rank is required to look prices up.'}
          {' '}<span className="text-zinc-600">Signed in as rank {rank}.</span>
        </p>
      )}

      {loading ? (
        <div className="space-y-3">
          {[0, 1].map(i => <div key={i} className="h-40 rounded-2xl bg-[#121417] border border-[#2A2D31] animate-pulse"/>)}
        </div>
      ) : records.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#2A2D31] bg-[#121417] py-14 px-6 text-center">
          <Check className="w-10 h-10 mx-auto mb-3 text-emerald-500/50"/>
          <p className="font-bold text-white mb-1">Nothing owed</p>
          <p className="text-xs text-zinc-500 max-w-md mx-auto leading-relaxed">
            Every deposit the bots received has been priced and credited. Records land here when an
            item is missing from the catalog and its price cannot be verified against both sources.
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs text-zinc-500">
            {records.length} record{records.length === 1 ? '' : 's'} · {totalItems} item{totalItems === 1 ? '' : 's'} owed
          </p>
          <div className="space-y-3">
            {records.map(r => (
              <PendingRecord key={r.id} record={r} index={index} multiplier={multiplier}
                viewerRank={rank} onResolved={load} onCatalogChanged={loadCatalog}/>
            ))}
          </div>
        </>
      )}
    </motion.div>
  );
};

export default Mm2pendingitemreviews;